import { useCallback, useEffect, useRef, useState } from "react";
import { AttenteRedaction, useSignalerPret } from "./AttenteRedaction";
import { PLANS } from "@riseafrica/scoring";
import { CONFIG } from "../config";
import { MESSAGE_EQUIPE } from "../content/copy";
import { delaiConservation } from "../content/editeur";
import { PAYS, PAYS_PRINCIPAUX, numeroInternational, paysParCode, phrasePortefeuilles } from "../lib/pays";
import { fmt } from "../lib/format";
import {
  lireStatutPaiement,
  ouvrirPaiement,
  relancerRapport,
  simulerPaiement,
  type StatutPaiement,
} from "../lib/api";
import {
  dessinerCarteGain,
  dessinerCarteProgression,
  nomCarteGain,
  nomCarteProgression,
  partagerImage,
  phraseCarteProgression,
} from "../lib/carte";
import { cheminDeParrainage, shareWhatsApp } from "../lib/share";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";

/**
 * Tunnel de paiement.
 *
 * Ce composant **ne confirme jamais un paiement de lui-même**. Il ouvre la
 * session, envoie l'utilisateur chez l'opérateur, puis interroge
 * `/pay/status` — dont la réponse vient d'un webhook opérateur authentifié.
 * Afficher « paiement confirmé » parce qu'un `fetch` a renvoyé 200 reviendrait
 * à faire confiance au navigateur pour dire qu'on a été payé : c'est
 * exactement ce que la règle non négociable n° 3 interdit, et la première
 * chose qu'un utilisateur curieux tenterait.
 */

/** Cadence d'interrogation, et durée au bout de laquelle on cesse d'attendre. */
const INTERVALLE_MS = 3000;

/**
 * Dix minutes.
 *
 * Cinq ne suffisaient pas, et la faute était arithmétique : la rédaction du
 * rapport dispose de neuf minutes côté serveur (`timeoutSeconds: 540`). La
 * fenêtre cessait donc d'attendre **avant** que le document qu'elle attend
 * puisse exister, et concluait sur un message d'abandon alors que tout se
 * passait bien.
 */
const ATTENTE_MAX_MS = 10 * 60 * 1000;

/**
 * Demander le remboursement, sans avoir à écrire à personne.
 *
 * La demande est **datée et rattachée à la transaction** : elle apparaît dans
 * le tableau de bord et ne peut plus se perdre. Le mouvement d'argent, lui,
 * reste manuel — il passe par l'opérateur, et prétendre l'automatiser ici
 * serait promettre plus qu'on ne tient.
 */
function Remboursement({ txId }: { txId: string }) {
  const [etat, setEtat] = useState<"repos" | "envoi" | "faite" | "erreur">("repos");
  const [message, setMessage] = useState("");

  if (etat === "faite") {
    return (
      <p className="fine" aria-live="polite" style={{ marginTop: "16px" }}>
        {message}
      </p>
    );
  }

  return (
    <details className="remb">
      <summary>Demander le remboursement</summary>
      <p className="fine">
        Intégral, sans justification à fournir, dans les quatorze jours. Votre demande est
        enregistrée et datée ; le remboursement part par le moyen de paiement utilisé.
      </p>
      <button
        type="button"
        className="btn btn-ghost"
        disabled={etat === "envoi"}
        onClick={() => {
          setEtat("envoi");
          void fetch(CONFIG.payRemboursementEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ txId }),
          })
            .then(async (r) => {
              const corps = (await r.json()) as { message?: string };
              if (!r.ok) throw new Error(corps.message ?? "");
              setMessage(corps.message ?? "Demande enregistrée.");
              setEtat("faite");
            })
            .catch(() => {
              setMessage("");
              setEtat("erreur");
            });
        }}
      >
        {etat === "envoi" ? "Enregistrement…" : "Confirmer la demande"}
      </button>
      {etat === "erreur" && (
        <p className="fine" aria-live="polite">
          L'enregistrement n'a pas abouti. Réessayez : votre droit au remboursement n'en dépend
          pas, seul son enregistrement a échoué.
        </p>
      )}
    </details>
  );
}

/**
 * Ce qui peut recevoir le focus dans la fenêtre.
 *
 * `tabindex="-1"` est exclu délibérément : ces éléments se focalisent par
 * programme — c'est ainsi qu'on pose le curseur sur un conteneur — mais la
 * tabulation ne s'y arrête pas, et les inclure ferait boucler le piège sur une
 * cible que personne n'atteint au clavier.
 */
const FOCALISABLES =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Les raisons proposées au départ ; chacune est un compteur (voir `mesures.ts`). */
const RAISONS = [
  { evenement: "retenu_prix", libelle: "Le prix" },
  { evenement: "retenu_apercu", libelle: "Je veux en voir plus avant" },
  { evenement: "retenu_plus_tard", libelle: "Pas maintenant" },
  { evenement: "retenu_paiement", libelle: "Mon moyen de paiement ne convient pas" },
  { evenement: "retenu_confiance", libelle: "Je ne connais pas STRATERA" },
] as const;

export function PayModal() {
  const {
    payPlan,
    closePay,
    reportPrice,
    prixEntreprise,
    say,
    t1,
    t2,
    t3,
    achat,
    noterAchat,
    oublierLAchat,
    entreprise,
  } = useApp();
  const [step, setStep] = useState(1);
  const [operateur, setOperateur] = useState<string | null>(null);
  /** Le pays de l'acheteur : il décide de l'indicatif, donc de la caisse ouverte. */
  const [paysCode, setPaysCode] = useState<string>("CI");
  const [numero, setNumero] = useState("");
  const [consent, setConsent] = useState(true);
  /*
    « Je travaille à mon compte » — posé à l'entrée, confirmé ici.

    La première version posait la question à cette étape, en faisant valoir
    qu'elle ne change que le document payé. C'était vrai du document et faux de
    la vente : un indépendant parcourait un écran de résultat écrit pour un
    salarié, puis une offre qui lui promettait « la trajectoire de
    repositionnement la plus courte », et ne rencontrait la case qu'après avoir
    décidé d'acheter. Rien ne lui disait que le produit était pour lui tant
    qu'il pouvait encore renoncer.

    Elle se pose donc à l'accueil, et vit dans l'état partagé. Elle reste
    modifiable ici — c'est le dernier écran avant que le rapport ne se rédige,
    et quelqu'un qui a coché trop vite doit pouvoir se reprendre sans repasser
    par l'accueil.
  */
  const { aSonCompte, setASonCompte, statutDeclare, titresPlan } = useApp();
  const [numeroInvalide, setNumeroInvalide] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /*
    L'identité d'acheteur ne se demande que si l'opérateur l'exige : le
    serveur répond alors « acheteur_requis » et les champs apparaissent. La
    demander d'office reviendrait à collecter trois données personnelles pour
    des opérateurs qui n'en veulent pas — et en simulation, pour une
    transaction qui n'aura pas lieu.
  */
  const { identiteAcheteurRequise, codeAccesRequis, conservationContactsJours } = useApp();
  /*
    La durée annoncée est celle que le serveur applique — 11/10/2026. L'écran
    écrivait « quatre-vingt-dix jours » en dur pendant que la purge en tenait
    cent quatre-vingts. Sans réponse des réglages, on renvoie à la politique
    plutôt que d'afficher un chiffre inventé.
  */
  const dureeAdresse = delaiConservation(conservationContactsJours);
  /*
    L'exigence est **connue d'avance** depuis le 25/08.

    Ces deux drapeaux valaient faux au départ et ne passaient à vrai qu'après
    un refus du serveur. La règle — ne demander que ce que l'opérateur exige —
    ne change pas ; c'est le moment où on la lit qui change. Avec un seul
    opérateur branché qui réclame toujours l'identité, et un rodage fermé qui
    réclame toujours le code, l'ancien ordre garantissait deux refus à chaque
    acheteur, et laissait une transaction morte au registre.

    Le repli reste l'ancien comportement : si `/api/reglages` n'a pas répondu,
    les drapeaux sont faux et le serveur révèle les champs comme avant. Dégradé,
    jamais bloquant.
  */
  const [identiteRequise, setIdentiteRequise] = useState(identiteAcheteurRequise);
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [identiteInvalide, setIdentiteInvalide] = useState(false);
  /*
    Recevoir le rapport par courriel — T22, coché par défaut.

    L'envoi du document acheté relève de l'exécution du contrat, pas de la
    prospection : il n'exige pas de consentement. La case cochée rend donc le
    service par défaut, et la laisser décochable respecte celui qui ne veut
    aucune trace — le rapport reste alors sur la page, exactement comme avant.
    Personne ne perd un service en refusant.
  */
  const [courriel, setCourriel] = useState(true);
  /*
    Le rendez-vous des quatre-vingt-dix jours. **Décoché**, et c'est la
    différence qui compte avec la case au-dessus : celle-ci recontacte, l'autre
    livre ce qui a été payé.
  */
  const [rappel, setRappel] = useState(false);
  /*
    Le point d'étape à trente jours — 28/09/2026. Une troisième case,
    décochée, qui n'existe que si le suivi est allumé et le rendez-vous des
    quatre-vingt-dix jours accepté. Le message des quatre-vingt-dix jours se
    disait « le seul de cette nature » : en ajouter un sans le demander
    rendrait cette phrase fausse.
  */
  const [etape30, setEtape30] = useState(false);
  const { progressionActive } = useApp();
  /*
    Le rodage fermé : le serveur verrouille l'encaissement réel derrière un
    code d'accès (PAY_ACCES_CODE). Le champ n'apparaît que si le serveur le
    réclame — l'état d'après-lancement, sans code, ne montre rien.
  */
  const [codeRequis, setCodeRequis] = useState(codeAccesRequis);
  const [codeAcces, setCodeAcces] = useState("");
  const [codeRefuse, setCodeRefuse] = useState(false);
  const [txId, setTxId] = useState<string | null>(null);
  const [statut, setStatut] = useState<StatutPaiement>("en_attente");
  /** L'adresse de la caisse, gardée pour pouvoir y renvoyer. */
  const [urlPaiement, setUrlPaiement] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [urlRapport, setUrlRapport] = useState<string | null>(null);
  /*
    Ce que le rapport annonce rendre, par semaine.

    Il n'existe qu'une fois le document rédigé — c'est le total des dix
    actions —, donc il arrive par `/pay/status` en même temps que le lien, et
    jamais avant. Une valeur devinée plus tôt serait une promesse de gain, ce
    que ni la règle n° 6 ni le prompt du rapport n'autorisent.
  */
  const [gainHebdo, setGainHebdo] = useState<number | null>(null);
  const [progressionFaite, setProgressionFaite] = useState<{ heures: number; jours: number } | null>(null);
  /** Où en est la rédaction, pendant l'attente. Voir `ETAPES`. */
  const [etape, setEtape] = useState<string | null>(null);
  useSignalerPret(urlRapport !== null);

  /*
    Les réglages arrivent après le premier rendu du composant.

    Sans ce suivi, un tunnel ouvert dans la seconde qui précède la réponse du
    serveur garderait les drapeaux à faux pour toute sa durée — et rejouerait
    exactement les deux refus qu'on supprime. On ne fait que **révéler** : un
    champ déjà affiché ne se cache pas sous les doigts de quelqu'un en train
    d'y écrire.
  */
  useEffect(() => {
    if (identiteAcheteurRequise) setIdentiteRequise(true);
  }, [identiteAcheteurRequise]);
  useEffect(() => {
    if (codeAccesRequis) setCodeRequis(true);
  }, [codeAccesRequis]);
  const [expire, setExpire] = useState(false);
  // Connu **avant** l'ouverture, par la configuration de construction, et
  // reconfirmé par le serveur à l'étape 3. Le savoir seulement après avoir
  // demandé un numéro revenait à collecter une donnée personnelle pour une
  // transaction qui n'aurait pas lieu.
  const [simule, setSimule] = useState(CONFIG.paiementSimule);
  const [codeSimule, setCodeSimule] = useState("");
  const [erreurCode, setErreurCode] = useState<string | null>(null);
  const [simulationEnCours, setSimulationEnCours] = useState(false);
  const [relanceEnCours, setRelanceEnCours] = useState(false);
  /*
    Un bouton qui ne peut pas aboutir est un faux signal.

    Le 20/08/2026, l'écran a répondu « les données de votre diagnostic ne sont
    plus rattachées à cet achat » — et a laissé sous ce texte un bouton
    « Relancer la rédaction » qui ne pouvait que redonner le même refus. Quand
    le serveur dit que la reprise est close, le bouton disparaît.
  */
  const [relanceClose, setRelanceClose] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  /** La question de sortie est affichée — une fois par ouverture, jamais imposée. */
  const [sortie, setSortie] = useState(false);

  /*
    Les trois plans, pas deux. Le plan entreprise retombait sur le prix du
    rapport salarié : la fenêtre affichait « Payer 3 000 F » à un dirigeant
    que le serveur allait débiter de 15 000 F — le serveur recalcule et fait
    foi, mais annoncer un montant et en encaisser un autre est exactement le
    faux signal que la règle n° 6 interdit.
  */
  const montant =
    payPlan === "bundle"
      ? CONFIG.priceBundle
      : payPlan === "entreprise"
        ? // Le prix dirigeant vient du serveur quand il a répondu : c'est lui
          // qui sait ce que Chariow facture réellement. Sinon, celui du module
          // partagé — la bonne valeur dans le cas ordinaire.
          (prixEntreprise ?? PLANS.entreprise)
        : reportPrice;

  useEffect(() => {
    if (payPlan === null) return;
    setStep(1);
    setSortie(false);
    setOperateur(null);
    setPaysCode("CI");
    setNumero("");
    setConsent(true);
    setNumeroInvalide(false);
    setEnvoiEnCours(false);
    setErreur(null);
    // Ce que l'opérateur exige, connu d'avance — 09/10/2026. Remettre à faux
    // ici défaisait l'effet de synchronisation, qui ne rejoue pas : chaque
    // acheteur essuyait un refus muet au premier « Payer ».
    setIdentiteRequise(identiteAcheteurRequise);
    setPrenom("");
    setNom("");
    setEmail("");
    setIdentiteInvalide(false);
    setCourriel(true);
    setCodeRequis(codeAccesRequis);
    setCodeAcces("");
    setCodeRefuse(false);
    setTxId(null);
    setStatut("en_attente");
    setUrlPaiement(null);
    setMessage("");
    setUrlRapport(null);
    setExpire(false);
    setSimule(CONFIG.paiementSimule);
    setSimulationEnCours(false);
    setRelanceEnCours(false);

    /*
      Reprise. Une transaction déjà ouverte pour ce même plan est reprise là où
      elle en était, au lieu d'en ouvrir une seconde. Sans cela, l'acheteur qui
      revient sur la page — rechargement, retour depuis l'opérateur, fenêtre
      fermée par mégarde — se voyait proposer de payer une deuxième fois ce
      qu'il avait déjà payé.
    */
    const reprise = achat !== null && achat.plan === payPlan;
    if (reprise) {
      setTxId(achat.tx);
      setStep(3);
    }
    /*
      Une reprise n'est pas une ouverture — 12/10/2026. Elle saute droit à
      l'attente de l'opérateur, sans choix de pays : la compter comme
      `pay_open` la faisait lire comme un abandon avant le pays.
    */
    track(reprise ? "pay_reprise" : "pay_open", { plan: payPlan, amount: montant, reprise });
    // `montant` est dérivé de `payPlan` : la réinitialisation suit l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPlan]);

  /*
    Le retour d'Android ferme aussi la fenêtre — 13/10/2026.

    La fenêtre a sa propre entrée d'historique : le geste « retour » la referme
    sans passer par la croix, donc sans `pay_close`. Or c'est la sortie la plus
    naturelle sur un téléphone, et la seule possible dans le navigateur de
    Facebook. Toute fermeture que la fenêtre n'a pas faite elle-même est donc
    notée ici, avec l'étape où elle a eu lieu.
  */
  const fermetureSuivie = useRef(false);
  const etapeCourante = useRef(step);
  etapeCourante.current = step;
  useEffect(() => {
    if (payPlan === null) return;
    fermetureSuivie.current = false;
    return () => {
      if (!fermetureSuivie.current) track("pay_close", { step: etapeCourante.current, via: "retour" });
    };
  }, [payPlan]);
  const fermerFenetre = useCallback(() => {
    fermetureSuivie.current = true;
    closePay();
  }, [closePay]);

  useEffect(() => {
    if (payPlan === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        track("pay_close", { step: "esc" });
        fermerFenetre();
        return;
      }
      /*
        La tabulation reste dans la fenêtre — 02/09/2026.

        Elle porte `aria-modal="true"`, ce qui **déclare** au lecteur d'écran
        que le reste de la page est inerte. La tabulation, elle, en sortait
        librement : au bout de quelques pressions, le curseur se promenait dans
        une page que la fenêtre venait de déclarer hors service, sur des
        commandes qu'on ne voyait plus.

        Une déclaration que le clavier dément est pire qu'une absence de
        déclaration : elle fait confiance à qui ne peut pas vérifier. Et c'est
        l'écran où de l'argent change de mains.
      */
      if (event.key !== "Tab") return;
      const boite = boxRef.current;
      if (boite === null) return;

      const cibles = [...boite.querySelectorAll<HTMLElement>(FOCALISABLES)].filter(
        // `getClientRects` plutôt qu'`offsetParent` : la fenêtre est en
        // `position: fixed`, où `offsetParent` vaut `null` pour tout le monde
        // et écarterait donc **toutes** les cibles.
        (element) => element.getClientRects().length > 0 && !element.hasAttribute("disabled"),
      );
      if (cibles.length === 0) return;

      const premier = cibles[0];
      const dernier = cibles[cibles.length - 1];
      const actif = document.activeElement;
      if (!boite.contains(actif)) {
        event.preventDefault();
        premier.focus();
      } else if (event.shiftKey && actif === premier) {
        event.preventDefault();
        dernier.focus();
      } else if (!event.shiftKey && actif === dernier) {
        event.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [payPlan, fermerFenetre]);

  /**
   * Le focus revient d'où il vient — 02/09/2026.
   *
   * À la fermeture, il retombait sur le corps du document : la tabulation
   * suivante repartait du tout premier lien de la page, et il fallait la
   * parcourir en entier pour revenir au bouton d'achat qu'on venait de
   * quitter. Sur l'écran de résultat, ce bouton est vers la fin.
   *
   * Le déclencheur est `ouvert` et non `payPlan` : ce dernier peut changer sans
   * que la fenêtre se ferme, et le nettoyage volerait alors le focus au beau
   * milieu d'une saisie.
   *
   * `document.contains` avant de rendre le focus : entre l'ouverture et la
   * fermeture, l'écran a pu changer — un résultat livré remplace le bouton
   * d'achat — et forcer le focus sur un élément détaché ne fait rien du tout,
   * silencieusement.
   */
  const ouvert = payPlan !== null;
  useEffect(() => {
    if (!ouvert) return;
    const ouvreur = document.activeElement as HTMLElement | null;
    return () => {
      if (ouvreur && document.contains(ouvreur)) ouvreur.focus({ preventScroll: true });
    };
  }, [ouvert]);

  useEffect(() => {
    if (payPlan === null) return;
    // La croix de fermeture est le premier bouton du DOM ; la sauter, sinon
    // ouvrir le paiement place le focus sur « abandonner ».
    boxRef.current
      ?.querySelector<HTMLElement>("input, button:not(.fermer-modale)")
      ?.focus();
  }, [payPlan, step]);

  /**
   * Interroge le serveur jusqu'à ce qu'il tranche.
   *
   * **`paye` n'est pas une fin.** C'est l'erreur que corrige cette boucle : elle
   * s'arrêtait dès que l'opérateur avait confirmé, alors que le paiement n'est
   * que la moitié de la transaction. L'adresse du rapport n'apparaît qu'au
   * passage en `livre`, une fois la rédaction terminée — jusqu'à neuf minutes
   * plus tard. L'écran affichait donc « le lien de téléchargement apparaît ici
   * dès qu'il est prêt » après avoir cessé d'écouter : une promesse que le code
   * ne pouvait plus tenir, sur le seul écran où de l'argent a changé de mains.
   *
   * On continue donc tant que la transaction est en mouvement, et on ne s'arrête
   * que sur un état terminal : rapport livré, paiement perdu, ou rédaction
   * échouée — cette dernière ouvrant droit au remboursement, ce que dit le
   * message du serveur.
   */
  useEffect(() => {
    if (txId === null || step !== 3) return;
    let vivant = true;
    let confirmeSignale = false;
    const debut = Date.now();

    const interroger = async () => {
      const etat = await lireStatutPaiement(txId);
      if (!vivant) return;
      if (etat) {
        setStatut(etat.statut);
        setMessage(etat.message);
        if (etat.urlRapport) setUrlRapport(etat.urlRapport);
        if (typeof etat.gainHebdo === "number") setGainHebdo(etat.gainHebdo);
        if (typeof etat.heuresRendues === "number" && typeof etat.joursProgression === "number") {
          setProgressionFaite({ heures: etat.heuresRendues, jours: etat.joursProgression });
        }
        if (typeof etat.etape === "string") setEtape(etat.etape);

        // La confirmation s'annonce une seule fois, au premier passage à un
        // état payé — sinon l'annonce vocale se répéterait toutes les trois
        // secondes pendant toute la rédaction.
        if (!confirmeSignale && etat.statut !== "en_attente") {
          confirmeSignale = true;
          if (etat.statut === "paye" || etat.statut === "livre") {
            say("Paiement confirmé par l'opérateur");
            track("pay_success", { op: operateur, amount: montant, plan: payPlan });
          }
        }

        // Livré : le lien est là, il n'y a plus rien à attendre.
        if (etat.statut === "livre" && etat.urlRapport) {
          say("Votre rapport est prêt");
          return;
        }
        // Payé mais non rédigé : la garantie s'applique, et le serveur le dit.
        // Continuer à interroger n'apprendrait plus rien.
        if (etat.statut === "echec_livraison") return;
        if (etat.statut === "echoue" || etat.statut === "annule") {
          // Une transaction qui n'aboutit pas ne doit pas rester en travers du
          // chemin : sans cet oubli, l'écran continuerait de proposer « récupérer
          // mon rapport » pour un paiement qui n'a jamais eu lieu.
          oublierLAchat();
          track("pay_error", { op: operateur, statut: etat.statut });
          return;
        }
      }
      if (Date.now() - debut > ATTENTE_MAX_MS) {
        setExpire(true);
        return;
      }
      window.setTimeout(() => void interroger(), INTERVALLE_MS);
    };

    void interroger();
    return () => {
      vivant = false;
    };
  }, [txId, step, say, operateur, montant, payPlan, oublierLAchat]);

  /** Le profil sert à rédiger le rapport : sans lui, il serait générique. */
  const { profilProgression } = useApp();
  const profil = useCallback(() => {
    // Un rapport de progression porte son propre profil, bâti depuis la ligne
    // de base : la personne arrive d'un lien, sans parcours derrière elle.
    // Le statut suit la case de cette fenêtre, comme pour tout autre achat :
    // se corriger ici doit changer le document.
    if (profilProgression) {
      return {
        ...profilProgression,
        aSonCompte,
        gestion: aSonCompte ? profilProgression.gestion : null,
      };
    }
    if (!t1) return null;
    return {
      metier: t1.metier,
      metierNormalise: t1.metier.toLowerCase(),
      groupe: t1.exposure.groupe,
      taches: t1.taches.map((t) => ({
        texte: t.texte,
        niveau: t.niveau,
        heures: t.heures ?? 0,
      })),
      // La gestion ne compte que pour quelqu'un à son compte (T38) : décocher
      // la case au paiement retire ses heures **et** leur valeur.
      valeurAnnuelle: t2 ? (aSonCompte ? t2.money : (t2.moneyMetier ?? t2.money)) : null,
      maitrise: t3?.level.niveau ?? null,
      aSonCompte,
      gestion: aSonCompte ? (t2?.heuresGestion ?? null) : null,
      titres: titresPlan,
    };
  }, [t1, t2, t3, aSonCompte, titresPlan, profilProgression]);

  if (payPlan === null) return null;

  /*
    Une question au moment de partir — 12/10/2026.

    Quinze ouvertures sur dix-sept se refermaient au premier écran, et
    personne ne pouvait dire pourquoi : le produit ne recontacte pas, et c'est
    voulu. On le demande donc une fois, au départ, en un clic. Jamais après
    l'envoi au paiement (étape 3), jamais en simulation, et toujours avec
    « Fermer sans répondre » : une question qu'on ne peut pas refuser serait
    un mur, pas une mesure. Échap ferme sans rien demander.
  */
  const fermer = () => {
    if (!simule && step < 3 && !sortie) {
      track("pay_close", { step, statut });
      setSortie(true);
      return;
    }
    if (!sortie) track("pay_close", { step, statut });
    fermerFenetre();
  };

  if (sortie) {
    const repondre = (motif: (typeof RAISONS)[number]["evenement"]) => {
      track(motif, { step });
      fermerFenetre();
    };
    return (
      <div className="modal" role="dialog" aria-modal="true" aria-label="Avant de partir">
        <div className="box" ref={boxRef}>
          <button type="button" className="fermer-modale" onClick={fermerFenetre} aria-label="Fermer">
            ×
          </button>
          <h3>Qu'est-ce qui vous retient ?</h3>
          <p className="fine" style={{ marginTop: 0 }}>
            Un clic, sans rien d'autre à donner. C'est la seule façon pour nous de savoir quoi
            améliorer.
          </p>
          <div className="oprow">
            {RAISONS.map((r) => (
              <button key={r.evenement} type="button" onClick={() => repondre(r.evenement)}>
                {r.libelle}
              </button>
            ))}
          </div>
          <button type="button" className="lien-cible" onClick={() => setSortie(false)}>
            Revenir au paiement
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginTop: "14px" }}
            onClick={fermerFenetre}
          >
            Fermer sans répondre
          </button>
        </div>
      </div>
    );
  }

  const payer = async () => {
    /*
      Le numéro n'est exigé que là où il est demandé.

      En simulation, le champ n'existe pas — c'est voulu : on ne réclame pas
      une donnée personnelle pour une transaction qui n'aura pas lieu. Mais le
      contrôle, lui, était resté. Il refusait donc systématiquement, en
      **silence** : le message d'erreur s'affiche à côté d'un champ absent de
      l'écran. Le tunnel d'achat ne s'ouvrait plus du tout, dans le seul mode
      aujourd'hui disponible, et rien ne le disait.
    */
    if (!simule && numero.replace(/\D/g, "").length < 8) {
      setNumeroInvalide(true);
      return;
    }
    setNumeroInvalide(false);
    // Quand l'opérateur a déjà réclamé l'identité, elle se contrôle ici — un
    // aller-retour serveur pour apprendre qu'un champ est vide serait un
    // détour sans information.
    if (identiteRequise && (!prenom.trim() || !nom.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) {
      setIdentiteInvalide(true);
      return;
    }
    setIdentiteInvalide(false);
    if (codeRequis && codeAcces.trim() === "") {
      setCodeRefuse(true);
      return;
    }
    setCodeRefuse(false);
    /*
      Rien ne part sans le profil qui permet de rédiger.

      Le 20/08/2026, un achat de 3 000 F est parti avec `profil: null` : le
      diagnostic n'était plus en mémoire — un rechargement de page suffit — et
      rien, ni ici ni côté serveur, ne l'a arrêté. Le serveur refuse désormais
      d'encaisser dans ce cas ; on le dit un cran plus tôt, avec la seule
      phrase utile à quelqu'un devant l'écran : ce qu'il doit refaire.
    */
    if (payPlan !== "entreprise" && profil() === null) {
      setEnvoiEnCours(false);
      setErreur(
        "Les réponses de votre diagnostic ne sont plus en mémoire — un rechargement de page suffit à les perdre. Relancez le diagnostic avant de payer : personne ne doit payer un rapport qui ne pourrait pas être rédigé.",
      );
      return;
    }
    setEnvoiEnCours(true);
    setErreur(null);
    track("pay_submit", { op: operateur, amount: montant });

    try {
      const ouverture = await ouvrirPaiement({
        op: operateur ?? "wave",
        plan: payPlan,
        msisdn: numeroInternational(numero, paysParCode(paysCode) ?? PAYS[0]),
        consent,
        acheteur: identiteRequise
          ? { email: email.trim(), prenom: prenom.trim(), nom: nom.trim() }
          : null,
        courriel,
        rappel,
        etape30: rappel && etape30 && progressionActive && payPlan !== "entreprise",
        codeAcces: codeRequis ? codeAcces.trim() : null,
        // Chaque plan porte son profil : le rapport salarié se rédige sur les
        // tâches du diagnostic, le rapport entreprise sur les postes déclarés
        // à l'écran dirigeant. Envoyer l'un pour l'autre livrerait le mauvais
        // document à quelqu'un qui vient de payer.
        profil: payPlan === "entreprise" ? null : profil(),
        profilEntreprise: payPlan === "entreprise" ? entreprise : null,
      });
      setTxId(ouverture.txId);
      // Retenu ici, et pas à la confirmation : c'est entre l'ouverture et le
      // retour de l'opérateur que la trace se perdait.
      noterAchat(ouverture.txId, payPlan);
      setSimule(ouverture.simule === true);
      setStep(3);
      setEnvoiEnCours(false);
      // L'utilisateur finit le paiement chez l'opérateur. Le retour se fait par
      // webhook ; cette fenêtre ne fait qu'attendre.
      //
      // En simulation, aucun opérateur n'existe : on n'ouvre rien et on affiche
      // les trois issues à déclencher à la main.
      if (!ouverture.simule && ouverture.urlPaiement) {
        setUrlPaiement(ouverture.urlPaiement);
        /*
          Le nouvel onglet, et ce qu'on fait quand il ne s'ouvre pas.

          Relevé sur les échecs réels de la boutique le 25/08/2026 : **quinze
          sur vingt venaient du navigateur intégré de Facebook**. Celui-là
          bloque `window.open` sans le dire — et l'appel ne rendait rien qu'on
          regardait. La personne restait alors sur « validez sur votre
          téléphone » à attendre une notification qui n'était jamais partie,
          faute d'avoir atteint la page de paiement.

          Deux motifs d'échec de l'opérateur se lisent autrement une fois cela
          su : « le client n'a pas autorisé le paiement » et les échecs sans
          motif du tout. Personne n'avait renoncé ; on ne les avait jamais
          emmenés jusqu'à la caisse.

          Quand l'onglet est refusé, on navigue dans celui-ci. La transaction
          est déjà notée par `noterAchat`, et le retour d'opérateur ramène sur
          la page, qui s'en souvient.
        */
        /*
          Sans « noopener » dans les options — 10/10/2026. Avec lui,
          `window.open` renvoie **toujours** null, onglet ouvert ou non : la
          page courante partait donc elle aussi vers la caisse, et l'acheteur
          se retrouvait avec deux pages de paiement et sans l'écran d'attente.
          Trouvé par le parcours de paiement réel (`e2e/reel`). L'onglet est
          détaché après coup : même protection, et la détection redevient vraie.
        */
        const onglet = window.open(ouverture.urlPaiement, "_blank");
        if (onglet) onglet.opener = null;
        else window.location.href = ouverture.urlPaiement;
      }
    } catch (error) {
      setEnvoiEnCours(false);
      // L'opérateur émet le reçu et exige l'identité de l'acheteur : ce n'est
      // pas une panne, on révèle les champs et la demande se rouvre.
      if (error instanceof Error && error.message === "acheteur_requis") {
        setIdentiteRequise(true);
        setErreur(null);
        return;
      }
      // Rodage fermé : le serveur veut un code d'accès. Premier refus, on
      // révèle le champ ; refus suivant, le code tapé était faux.
      if (error instanceof Error && error.message === "code_acces_requis") {
        if (codeRequis) setCodeRefuse(true);
        setCodeRequis(true);
        setErreur(null);
        return;
      }
      /*
        La rédaction est momentanément hors d'atteinte, et la vente s'est
        fermée d'elle-même.

        On ne dit pas « panne » : rien n'est cassé côté acheteur, et surtout on
        ne lui laisse pas croire qu'en réessayant tout de suite il obtiendra son
        rapport. On dit ce qui est — le service reprend quand la rédaction
        redevient possible — et on rappelle que le diagnostic gratuit, lui,
        reste entier.
      */
      if (error instanceof Error && error.message === "vente_suspendue") {
        setErreur(
          "La rédaction des rapports est momentanément suspendue, donc la vente l'est aussi : " +
            "nous préférons ne rien encaisser plutôt que vous faire attendre un document. " +
            "Aucun débit n'a eu lieu. Votre diagnostic, lui, reste accessible.",
        );
        track("pay_error", { error: "vente_suspendue" });
        return;
      }
      /*
        La vente fermée **à la main**, distincte de celle fermée sur un fait —
        02/09/2026.

        `etatVente()` ferme sur un incident et répond 503 `vente_suspendue` :
        ce cas-là était traité juste au-dessus. La fermeture décidée depuis
        `/exploitation`, elle, répond 409 `vente_fermee`, et tombait dans le
        cas par défaut : « Le paiement n'a pas pu être ouvert. »

        Une phrase de panne, sur une décision. Elle invite à réessayer tout de
        suite — ce qui échouera exactement pareil, autant de fois qu'on
        insistera — et elle fait porter au produit l'apparence d'un défaut au
        moment précis où quelqu'un allait payer.

        Elle est dite comme ce qu'elle est : une décision, temporaire, sans
        débit, et qui ne retire rien de ce qui est déjà à l'écran.
      */
      if (error instanceof Error && error.message === "vente_fermee") {
        setErreur(
          "La vente est momentanément fermée de notre côté — c'est une décision, pas une " +
            "panne, et il n'y a rien à réessayer dans l'immédiat. Aucun débit n'a eu lieu. " +
            "Votre diagnostic et votre indice restent accessibles.",
        );
        track("pay_error", { error: "vente_fermee" });
        return;
      }
      /*
        Et la formule à 9 000 F fermée, pour la même raison.

        Sa carte ne s'affiche que si l'accompagnement est déclaré assuré, donc
        ce refus ne se rencontre qu'en cas de course entre l'écran et le
        réglage. Il coûte une ligne, et sans elle il dirait « panne » de ce qui
        est un choix.
      */
      if (error instanceof Error && error.message === "plan_indisponible") {
        setErreur(
          "Cette formule n'est pas ouverte en ce moment. Aucun débit n'a eu lieu — le rapport " +
            "seul, lui, reste disponible.",
        );
        track("pay_error", { error: "plan_indisponible" });
        return;
      }
      /*
        Le limiteur de débit, qui n'est pas un refus mais une attente.

        Il compte par adresse IP, et derrière le NAT d'un opérateur mobile des
        gens qui ne se connaissent pas la partagent. Dire « le paiement n'a pas
        pu être ouvert » à quelqu'un qui n'a rien fait de mal, et le laisser
        conclure que le produit refuse son argent, est le plus mauvais des deux
        textes possibles : il suffisait d'attendre.
      */
      if (error instanceof Error && error.message === "trop_de_demandes") {
        setErreur(
          "Trop de demandes viennent de votre connexion en ce moment — c'est une limite de " +
            "sécurité, pas un refus. Attendez une minute et recommencez. Aucun débit n'a eu lieu.",
        );
        track("pay_error", { error: "trop_de_demandes" });
        return;
      }
      /*
        Le portefeuille choisi n'est pas branché.

        Notre écran nomme Wave, Orange Money et MTN MoMo parce que ce sont les
        portefeuilles de l'acheteur ; le choix définitif se fait sur la page de
        l'encaisseur. Si l'adaptateur demandé n'existe pas côté serveur, la
        cause est chez nous, et la personne peut faire quelque chose :
        reprendre avec le portefeuille proposé par défaut.
      */
      if (error instanceof Error && error.message === "operateur_indisponible") {
        setErreur(
          "Ce moyen de paiement n'est pas disponible en ce moment. Aucun débit n'a eu lieu — " +
            "reprenez avec celui qui est proposé par défaut.",
        );
        track("pay_error", { error: "operateur_indisponible" });
        return;
      }
      setErreur("Le paiement n'a pas pu être ouvert. Aucun débit n'a eu lieu.");
      track("pay_error", { error: String(error) });
    }
  };

  /**
   * Déclenche une issue simulée. C'est le serveur qui fabrique la notification
   * et la signe : ce bouton ne confirme rien par lui-même, il demande à
   * l'opérateur simulé d'émettre son webhook.
   */
  const declencher = async (issue: "reussi" | "echoue" | "annule") => {
    if (!txId) return;
    if (codeSimule.trim() === "") {
      setErreurCode("Code requis.");
      return;
    }
    setErreurCode(null);
    setSimulationEnCours(true);
    const accepte = await simulerPaiement(txId, issue, codeSimule.trim());
    setSimulationEnCours(false);
    if (!accepte) {
      // Le serveur ne dit pas si c'est le code ou autre chose, et c'est voulu.
      // Ce message suppose la cause la plus probable sans l'affirmer.
      setErreurCode("Refusé. Vérifiez le code, ou réessayez dans un moment.");
    }
  };

  // Quatre états à l'écran, pas trois. `echec_livraison` tombait auparavant
  // dans le cas par défaut : un acheteur débité dont la rédaction avait échoué
  // lisait « validez la demande sur votre téléphone », alors qu'il n'y avait
  // plus rien à valider et que le remboursement lui était dû.
  const confirme = statut === "paye" || statut === "livre";
  const perdu = statut === "echoue" || statut === "annule";
  const manque = statut === "echec_livraison";

  const relancer = async () => {
    if (txId === null) return;
    setRelanceEnCours(true);
    const issue = await relancerRapport(txId);
    setRelanceEnCours(false);
    if (!issue) {
      setMessage("La reprise n'a pas pu être demandée. Réessayez dans un moment.");
      return;
    }
    /*
      Un bouton qui ne peut pas aboutir est un faux signal.

      Quand le serveur répond `epuise` — quota atteint, ou diagnostic qui n'est
      plus rattaché à l'achat — la reprise disparaît. Elle restait affichée, et
      un acheteur pouvait la presser indéfiniment sous un message qui lui
      annonçait déjà le remboursement.
    */
    if (issue.epuise) setRelanceClose(true);
    if (issue.message) setMessage(issue.message);
    if (issue.epuise) setRelanceClose(true);
    if (issue.urlRapport) setUrlRapport(issue.urlRapport);
    if (typeof issue.gainHebdo === "number") setGainHebdo(issue.gainHebdo);
    if (issue.statut === "livre") setStatut("livre");
    /*
      La reprise est désormais **demandée**, pas exécutée dans la requête : le
      serveur remet la transaction en file et répond tout de suite. On repasse
      donc l'écran en attente, ce qui relance l'interrogation de `/pay/status`
      — sans quoi la fenêtre resterait sur « rapport non délivré » alors que la
      rédaction repart, et l'acheteur presserait le bouton en boucle.
    */
    if (issue.statut === "paye") {
      setStatut("paye");
      setExpire(false);
    }
  };
  const pret = statut === "livre" && urlRapport !== null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Paiement">
      <div className="box" ref={boxRef}>
        {/* La sortie était uniquement en bas, sous tout le contenu : sur
            téléphone il fallait faire défiler pour trouver comment sortir d'un
            écran de paiement. */}
        <button type="button" className="fermer-modale" onClick={fermer} aria-label="Fermer">
          ×
        </button>
        {simule && (
          <div className="simbanner">
            <b>Mode simulation.</b> Aucun paiement n'est réellement effectué et aucun montant n'est
            débité. Le webhook, la vérification de signature et la livraison du rapport suivent en
            revanche le chemin réel.
          </div>
        )}
        <div className="paystep">
          {simule ? (
            <>
              Étape <b>{step === 1 ? 1 : 2}</b> sur 2 · essai
            </>
          ) : (
            <>
              Étape <b>{step === 3 ? 3 : step}</b> sur 3 · {fmt(montant)} F
            </>
          )}
        </div>

        {step === 1 && (
          <div>
            {/*
              En simulation, l'opérateur retenu n'a aucun effet : la même
              notification signée est émise quel que soit le bouton pressé.
              Faire choisir revenait à demander une décision qui n'en est pas
              une — et à demander ensuite un numéro qui ne sert à rien.
            */}
            {simule ? (
              <>
                <h3>Essai du parcours d'achat</h3>
                <p className="fine" style={{ marginBottom: "16px" }}>
                  Aucun opérateur n'est encore branché : il n'y a donc ni moyen de paiement à
                  choisir, ni numéro à donner. Le webhook, la vérification de signature et la
                  livraison du rapport suivent en revanche le chemin réel.
                </p>
                {/* Laiton : sur l'écran qui encaisse, l'action qui fait
                    avancer ne peut pas avoir le même poids qu'« Annuler ». */}
                <button
                  type="button"
                  className="btn btn-brass"
                  disabled={envoiEnCours}
                  onClick={() => {
                    setOperateur("simulation");
                    track("pay_operator", { op: "simulation" });
                    void payer();
                  }}
                >
                  {envoiEnCours ? "Ouverture…" : "Ouvrir le parcours d'essai"}
                </button>
                {erreur && (
                  <div className="err" role="alert">
                    {erreur}
                  </div>
                )}
              </>
            ) : (
              <>
                {/*
                  Le pays, et non plus le portefeuille — 11/10/2026.

                  Le choix « Wave, Orange Money, MTN MoMo » n'orientait rien :
                  chez Chariow, le portefeuille se choisit sur sa page. Il
                  mentait en plus au Togo, où l'on paie par Mixx by Yas ou
                  Moov. Le pays, lui, fixe l'indicatif, donc la caisse que
                  Chariow ouvre. Voir `lib/pays.ts`.
                */}
                <h3>Dans quel pays payez-vous ?</h3>
                <div className="oprow">
                  {PAYS_PRINCIPAUX.map((p) => (
                    <button
                      key={p.code}
                      type="button"
                      onClick={() => {
                        setPaysCode(p.code);
                        setOperateur(p.code);
                        track("pay_operator", { op: p.code });
                        setStep(2);
                      }}
                    >
                      {p.nom} <span>+{p.indicatif}</span>
                    </button>
                  ))}
                </div>
                <div className="field">
                  <label htmlFor="pay-autrepays">Autre pays</label>
                  <select
                    id="pay-autrepays"
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setPaysCode(e.target.value);
                      setOperateur(e.target.value);
                      track("pay_operator", { op: e.target.value });
                      setStep(2);
                    }}
                  >
                    <option value="">Choisir…</option>
                    {PAYS.slice(PAYS_PRINCIPAUX.length).map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.nom} (+{p.indicatif})
                      </option>
                    ))}
                  </select>
                </div>
                {/*
                  Le montant vient de `montant`, celui-là même qui est affiché
                  dans le fil d'étapes juste au-dessus : deux chiffres écrits à
                  deux endroits finissent toujours par diverger.
                */}
                <p className="fine" style={{ margin: "0 0 16px" }}>
                  Vous payez {fmt(montant)} F, quel que soit le portefeuille. Les frais
                  d'encaissement sont à notre charge.
                </p>
              </>
            )}
            {/*
              Le rapport se sert du niveau de maîtrise : c'est lui qui décide si
              les dix actions parlent à quelqu'un qui débute ou à quelqu'un qui
              itère déjà. Sans le volet 3, il est écrit sans savoir à qui il
              s'adresse — et personne ne s'en aperçoit, ni l'acheteur ni nous.
              On le propose donc, en disant pourquoi. On ne l'impose pas : c'est
              l'écran qui encaisse, et une étape de plus s'y paie en abandons.

              **Sous l'action principale, jamais au-dessus.** Placé en tête, il
              était le premier bouton de la fenêtre de paiement — donc une
              invitation à en sortir, lue avant même le titre de l'écran et
              deux cent trente pixels avant l'action qu'on est venu faire.
            */}
            {/* Le volet 3 est un volet salarié : le proposer à un dirigeant
                qui achète le rapport de son entreprise serait un détour vers
                un questionnaire qui ne changerait rien à son livrable. */}
            {/*
              La ligne sur le volet 3 non passé est retirée — 11/10/2026.
              « Moins ajustées à votre niveau » semait le doute sur le
              document, sur l'écran où la personne vient de décider de payer.
              Le rapport s'écrit sans ce volet ; il n'y a rien à excuser.
            */}
            {/*
              Reconnaître, et non redemander — 26/08/2026.

              Cet écran reposait la question du statut à blanc — « Cochez si
              vous êtes indépendant » — à quelqu'un qui venait de la trancher
              sous le champ métier de l'accueil. Une question déjà répondue
              qu'on repose se lit comme un formulaire qui n'écoute pas, et
              elle tombait sur le seul écran où une friction coûte de l'argent.

              Deux cas, donc, et un seul contrôle par cas :

              — statut déclaré : une phrase qui dit ce que le rapport fera, et
                un lien pour corriger. Rien à cocher, rien à relire.
              — statut jamais déclaré (lien profond vers une page métier,
                session reprise) : la case d'origine, parce qu'ici la question
                n'a réellement jamais été posée.

              Corriger au paiement reste une déclaration : `statutDeclare` ne
              redescend pas, et la case ne revient donc pas après coup.
            */}
            {payPlan !== "entreprise" && (
              <div className="field" style={{ marginTop: "14px" }}>
                {statutDeclare ? (
                  <>
                    <p className="fine" style={{ margin: 0 }}>
                      {aSonCompte
                        ? "Vous avez indiqué travailler à votre compte : la dernière partie du rapport portera sur le repositionnement de votre offre — ce que vous vendez, à qui, et à quel prix."
                        : "Vous avez indiqué être salarié : la dernière partie du rapport portera sur une trajectoire de repositionnement vers un poste."}
                    </p>
                    {/*
                      Hors du paragraphe, et à la ligne.

                      Dans le fil de la phrase, ce bouton héritait sa couleur et
                      sa taille de `.fine` : même gris, mêmes 13 px, distingué
                      par un seul soulignement. On lisait « …et à quel prix.
                      Je suis salarié » — une contradiction, pas une commande.
                      Mesuré, pas jugé sur l'image : couleur identique à la
                      phrase, et le bouton commençait 44 px avant la fin de la
                      ligne, donc dans son flux.

                      Il descend d'un cran, s'annonce par un verbe, et reprend
                      la couleur du texte plein. La cible de 44 px vient de
                      `.lien-cible` et ne bouge pas.
                    */}
                    <button
                      type="button"
                      className="lien-cible corriger-statut"
                      onClick={() => setASonCompte(!aSonCompte)}
                    >
                      {aSonCompte
                        ? "Corriger : je suis salarié"
                        : "Corriger : je travaille à mon compte"}
                    </button>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="pay-soncompte"
                      style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
                    >
                      <input
                        type="checkbox"
                        id="pay-soncompte"
                        checked={aSonCompte}
                        onChange={(e) => setASonCompte(e.target.checked)}
                        style={{ width: "auto", marginTop: "3px" }}
                      />
                      <span>Je travaille à mon compte</span>
                    </label>
                    <p className="fine" style={{ margin: "6px 0 0 30px" }}>
                      {aSonCompte
                        ? "La dernière partie du rapport portera sur le repositionnement de votre offre — ce que vous vendez, à qui, et à quel prix — au lieu d'un poste à viser."
                        : "La dernière partie du rapport décrit un repositionnement vers un poste. Cochez si vous êtes indépendant : elle portera alors sur votre offre."}
                    </p>
                  </>
                )}
              </div>
            )}
            <p className="fine">
              {/* Un dirigeant n'a pas de « score » : il a un montant sous les yeux. */}
              {payPlan === "entreprise"
                ? "Vous ne payez qu'après avoir vu ce que ce temps coûte chez vous. Remboursé sur simple demande, sans justification, dans les quatorze jours."
                : "Vous ne payez qu'après avoir vu votre score. Remboursé sur simple demande, sans justification, dans les quatorze jours."}
            </p>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3>Votre numéro de téléphone</h3>
            <div className="field">
              <label htmlFor="paynum">Numéro de téléphone</label>
              <input
                type="tel"
                id="paynum"
                placeholder={paysCode === "CI" ? "07 00 00 00 00" : "Numéro local"}
                autoComplete="tel"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                style={numeroInvalide ? { borderColor: "var(--high)" } : undefined}
                aria-invalid={numeroInvalide}
                aria-describedby={numeroInvalide ? "paynum-erreur" : "paynum-aide"}
              />
              {/*
                L'indicatif demandé, et pourquoi — 25/08/2026.

                Le serveur préfixait tout numéro local par +225. Près de la
                moitié des acheteurs ne sont pas ivoiriens : un numéro togolais
                de huit chiffres devenait le numéro de quelqu'un d'autre. Le
                serveur refuse désormais ce qu'il ne sait pas situer ; l'écran
                doit donc demander l'indicatif avant, pas après le refus.
              */}
              <p id="paynum-aide" className="fine">
                {(() => {
                  const p = paysParCode(paysCode) ?? PAYS[0];
                  return (
                    <>
                      {p.nom} : l'indicatif +{p.indicatif} est ajouté pour vous.{" "}
                      {p.portefeuilles.length > 0
                        ? `Sur la page de paiement : ${phrasePortefeuilles(p)}.`
                        : "Le moyen de paiement se choisit sur la page de paiement."}{" "}
                      <button type="button" className="lien-cible" onClick={() => setStep(1)}>
                        Changer de pays
                      </button>
                    </>
                  );
                })()}
              </p>
              {numeroInvalide && (
                <p id="paynum-erreur" className="fine" style={{ color: "var(--high)" }}>
                  Numéro incomplet : au moins huit chiffres.
                </p>
              )}
            </div>

            {/* Le rodage fermé : l'encaissement réel vient d'ouvrir en cercle
                restreint, et le serveur exige un code d'accès. Le dire tel
                quel — un accès réservé est un fait, pas un artifice de
                rareté (règle n° 6). */}
            {codeRequis && (
              <div className="notice" aria-live="polite">
                <b>Accès en rodage.</b>
                <p className="fine" style={{ margin: "6px 0 10px" }}>
                  Le paiement réel vient d'ouvrir en cercle restreint : il faut un code d'accès
                  pour continuer. Si vous n'en avez pas, le diagnostic et vos résultats restent
                  entièrement gratuits.
                </p>
                <div className="field">
                  <label htmlFor="paycodeacces">Code d'accès</label>
                  <input
                    id="paycodeacces"
                    autoComplete="off"
                    value={codeAcces}
                    onChange={(e) => setCodeAcces(e.target.value)}
                    aria-invalid={codeRefuse}
                    aria-describedby={codeRefuse ? "paycodeacces-erreur" : undefined}
                  />
                </div>
                {codeRefuse && (
                  <p id="paycodeacces-erreur" className="fine" style={{ color: "var(--high)" }}>
                    Code refusé. Vérifiez-le auprès de la personne qui vous a invité.
                  </p>
                )}
              </div>
            )}

            {/* L'identité, seulement quand l'opérateur l'a réclamée : c'est
                lui qui émet le reçu. Elle lui est transmise, pas conservée —
                et le dire ici n'est pas une politesse, c'est la règle n° 5. */}
            {identiteRequise && (
              <div className="notice" aria-live="polite">
                <b>L'opérateur de paiement demande vos coordonnées de reçu.</b>
                <p className="fine" style={{ margin: "6px 0 10px" }}>
                  Prénom et nom servent à émettre votre reçu chez l'opérateur et ne sont pas
                  conservés par STRATERA. L'adresse électronique, si vous choisissez de recevoir votre
                  rapport par ce canal ci-dessous, est conservée {dureeAdresse ?? "pour la durée indiquée dans la politique de confidentialité"}. Le
                  diagnostic, lui, reste accessible sans aucune donnée nominative.
                </p>
                <div className="field">
                  <label htmlFor="payprenom">Prénom</label>
                  <input
                    id="payprenom"
                    autoComplete="given-name"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    aria-invalid={identiteInvalide && !prenom.trim()}
                  />
                </div>
                <div className="field">
                  <label htmlFor="paynom">Nom</label>
                  <input
                    id="paynom"
                    autoComplete="family-name"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    aria-invalid={identiteInvalide && !nom.trim()}
                  />
                </div>
                <div className="field">
                  <label htmlFor="payemail">Adresse électronique</label>
                  <input
                    id="payemail"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={identiteInvalide && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
                    aria-describedby={identiteInvalide ? "payidentite-erreur" : undefined}
                  />
                </div>
                {identiteInvalide && (
                  <p id="payidentite-erreur" className="fine" style={{ color: "var(--high)" }}>
                    Prénom, nom et adresse électronique valide sont nécessaires pour le reçu.
                  </p>
                )}
                {/*
                  Recevoir le rapport à cette adresse — T22.

                  La case n'apparaît que là où l'adresse est déjà demandée : on
                  ne réclame rien de plus que ce que l'opérateur exige pour son
                  reçu. Cochée par défaut, parce que c'est la livraison de ce
                  qui a été payé et non de la prospection ; décochable, parce
                  que le produit répète partout qu'il marche sans rien donner.

                  La rédaction prend jusqu'à neuf minutes, et la trace de
                  reprise vit dans ce navigateur seulement : sans courriel, qui
                  change de téléphone perd le rapport qu'il a payé.
                */}
                <label
                  htmlFor="pay-courriel"
                  className="fine"
                  style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginTop: "12px" }}
                >
                  <input
                    type="checkbox"
                    id="pay-courriel"
                    checked={courriel}
                    onChange={(e) => setCourriel(e.target.checked)}
                    style={{ width: "auto", marginTop: "3px" }}
                  />
                  <span>
                    Recevoir le lien de mon rapport à cette adresse — sans courriel, le lien ne vit
                    que dans ce navigateur.{" "}
                    {dureeAdresse
                      ? `Adresse conservée ${dureeAdresse}, puis effacée.`
                      : "Adresse effacée au terme indiqué dans la politique de confidentialité."}
                  </span>
                </label>
                {/*
                  Le rendez-vous des quatre-vingt-dix jours — 25/08/2026.

                  Une seconde case, **décochée**, et distincte de la première.
                  L'engagement du produit est écrit noir sur blanc côté serveur :
                  l'adresse « ne sert qu'à remettre le document — jamais à
                  recontacter, ce qui demanderait une autre base légale et un
                  autre consentement ». La phrase dit elle-même comment la lever.

                  Décochée, et non pré-cochée « pour le confort » : on ne
                  recueille pas un consentement en espérant que personne ne
                  décoche. Elle n'apparaît que si la première est cochée — sans
                  adresse gardée, il n'y a rien à promettre.

                  Ce qu'elle ouvre est **un seul message**, et l'écran le dit,
                  parce que c'est ce qui rend la case acceptable.
                */}
                {courriel && (
                  <label
                    htmlFor="pay-rappel"
                    className="fine"
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      marginTop: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="pay-rappel"
                      checked={rappel}
                      onChange={(e) => setRappel(e.target.checked)}
                      style={{ width: "auto", marginTop: "3px" }}
                    />
                    <span>
                      Me rappeler dans quatre-vingt-dix jours de refaire la mesure. Un seul
                      message, celui que votre rapport programme — jamais d'offre, jamais de
                      relance.
                    </span>
                  </label>
                )}
                {courriel && rappel && progressionActive && payPlan !== "entreprise" && (
                  <label
                    htmlFor="pay-etape30"
                    className="fine"
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      marginTop: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="pay-etape30"
                      checked={etape30}
                      onChange={(e) => setEtape30(e.target.checked)}
                      style={{ width: "auto", marginTop: "3px" }}
                    />
                    <span>
                      Et, à trente jours, une seule question : avez-vous commencé ? Un clic pour
                      répondre, et la première étape si ce n'est pas encore fait. Un second message,
                      jamais d'offre.
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* Consentement explicite et séparé : accepter de payer n'est pas
                accepter d'être recontacté. Et il ne se demande que si un envoi
                peut réellement avoir lieu : recueillir un accord pour un canal
                inactif, c'est demander une permission dont on ne fera rien. */}
            {CONFIG.messagerieActive && (
            <div className="field">
              {/* Un consentement se lit : ni capitales, ni monospace, ni 10,5 px. */}
              <label
                htmlFor="payconsent"
                className="label-phrase"
                style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
              >
                <input
                  type="checkbox"
                  id="payconsent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  style={{ width: "auto", marginTop: "4px" }}
                />
                <span className="fine" style={{ margin: 0 }}>
                  J'accepte de recevoir mon rapport et deux messages, à trois jours et à
                  quatre-vingt-dix jours, sur ce numéro WhatsApp — jamais d'offre. Sans cet accord,
                  le rapport reste disponible sur cette page.
                </span>
              </label>
            </div>
            )}

            <p className="fine" style={{ marginBottom: "14px" }}>
              En appuyant, la page de paiement sécurisée s'ouvre dans un nouvel onglet : vous y
              confirmez votre portefeuille, puis vous validez la demande sur votre téléphone. Rien
              n'est débité tant que vous ne l'avez pas validée.
            </p>
            <button type="button" className="btn btn-brass" onClick={payer} disabled={envoiEnCours}>
              {envoiEnCours ? "Ouverture…" : `Payer ${fmt(montant)} F`}
            </button>
            {erreur && (
              <div className="err" role="alert">
                {erreur}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="payok">
            {/*
              Trois états, et un seul dit « confirmé » : celui que le serveur
              tient d'un webhook opérateur authentifié.
            */}
            {confirme ? (
              <>
                <div className="tick">✓</div>
                <h3 style={{ marginTop: "12px" }}>
                  {pret ? "Votre rapport est prêt" : "Paiement confirmé par l'opérateur"}
                </h3>
                {/*
                  La phrase suit le titre — 26/08/2026.

                  Le titre passait à « Votre rapport est prêt », la phrase juste
                  en dessous continuait d'annoncer « en cours de rédaction », et
                  le bouton de téléchargement s'affichait entre les deux. Trois
                  états contradictoires sur le même écran, à l'instant précis où
                  quelqu'un qui vient de payer cherche à savoir s'il a reçu ce
                  qu'il a acheté.

                  En production le message du serveur masquait le défaut, parce
                  qu'il n'est jamais vide. Une condition qui ne tient que par la
                  valeur d'une autre variable ne tient pas.
                */}
                <p className="fine">
                  {pret
                    ? "Le lien de téléchargement est ci-dessous. Il reste accessible depuis cet appareil."
                    : message ||
                      (CONFIG.messagerieActive
                        ? "Votre rapport est en cours de rédaction. Il arrivera sur WhatsApp dès qu'il sera prêt."
                        : "Votre rapport est en cours de rédaction. Le lien de téléchargement apparaît ici dès qu'il est prêt.")}
                </p>
                {/*
                  Ce que la machine sait, dit à celui qui attend.

                  Le serveur écrit l'étape depuis le premier jour et ne la
                  rendait à personne. Quelqu'un qui vient de payer 3 000 F
                  regardait une phrase fixe pendant jusqu'à neuf minutes, sur un
                  téléphone, pendant qu'une machine à côté connaissait la réponse
                  à sa seule question : est-ce que ça avance.

                  Trois étapes, trois phrases courtes. Aucune promesse de durée :
                  on ne sait pas combien de temps la rédaction prendra, et
                  annoncer « plus que deux minutes » serait un faux signal sur la
                  seule chose que la personne surveille.
                */}
                {/* L'étape en toutes lettres reste annoncée aux lecteurs d'écran ; la
                    liste cochée, dessous, la montre. */}
                {!urlRapport && etape !== null && ETAPES[etape] && (
                  <p className="mini" aria-live="polite" style={{ marginTop: "-6px" }}>
                    <b>{ETAPES[etape]}</b>
                  </p>
                )}
                {/*
                  Téléchargement direct : aucune donnée nominative n'est requise
                  pour être servi, donc un acheteur sans WhatsApp doit pouvoir
                  récupérer ce qu'il a payé depuis cette page.
                */}
                {urlRapport ? (
                  <>
                    <p style={{ marginTop: "14px" }}>
                      <a className="btn btn-brass" href={urlRapport}>
                        Télécharger mon rapport
                      </a>
                    </p>
                    {/*
                      La référence, donnée à celui qui vient de payer.

                      Elle existait déjà — `txId` est un identifiant aléatoire
                      tiré par le serveur, et `/pay/status` rend le lien du
                      rapport à qui le présente. Elle n'était montrée à
                      personne. Tout ce qui reliait un acheteur à son achat
                      vivait donc dans le stockage de **ce** navigateur : un
                      téléphone changé, une navigation privée, un nettoyage, ou
                      les trente jours au bout desquels la trace s'efface, et
                      il n'avait plus rien.

                      Le produit tenait la clé et ne la donnait pas. La voici,
                      avec la phrase qui dit à quoi elle sert — sans quoi
                      personne ne la garde.
                    */}
                    {txId !== null && <Reference txId={txId} />}
                    {/*
                      La carte du temps récupéré, offerte après l'achat.

                      Ce qui se partageait jusqu'ici était un score, et un score
                      se compare. Du temps gagné se convoite : c'est la seule
                      phrase qu'une personne qui n'a pas encore acheté ait envie
                      de cliquer.

                      Le chiffre est celui imprimé sur la couverture du rapport,
                      calculé par le barème — révisé le 03/09/2026. Il venait des
                      dix actions écrites par le modèle, et la carte annonçait
                      alors autre chose que le document. Réservée aux acheteurs
                      parce qu'elle accompagne un rapport remis.
                    */}
                    {gainHebdo !== null && gainHebdo > 0 && t1 !== null && (
                      <CarteGain heures={gainHebdo} metier={t1.metier} />
                    )}
                    {/*
                      La carte de progression — 28/09/2026. Seulement après un
                      rapport de progression, seulement si des heures ont été
                      rendues : une carte « 0 h » ne sert personne, et une
                      carte négative encore moins.
                    */}
                    {progressionFaite !== null && progressionFaite.heures > 0 && profilProgression !== null && (
                      <CarteProgression
                        heures={progressionFaite.heures}
                        jours={progressionFaite.jours}
                        metier={profilProgression.metier}
                      />
                    )}
                    {payPlan === "entreprise" && (
                      <div className="block" style={{ marginTop: "16px" }}>
                        <h3>Vos salariés peuvent faire le leur</h3>
                        <p className="fine">
                          Le diagnostic individuel est gratuit et sans inscription : chacun voit ce
                          que l'IA change à son propre poste, et personne ne voit le résultat des
                          autres.
                        </p>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            shareWhatsApp(MESSAGE_EQUIPE);
                            track("partage_equipe", { voie: "whatsapp", depuis: "paiement" });
                          }}
                        >
                          Envoyer à votre équipe
                        </button>
                      </div>
                    )}
                    {/*
                      Le chemin du remboursement, à côté de la marchandise.

                      Les conditions de vente promettent « le remboursement sur
                      simple demande, sans justification ». Elles ne disaient pas
                      à qui l'adresser : une promesse sans chemin pour la
                      réclamer n'est pas une garantie, c'est une phrase — et
                      c'est l'acheteur mécontent, celui qu'on doit le mieux
                      traiter, qui s'en aperçoit.

                      Placé ici, et pas caché dans les mentions légales : offrir
                      la sortie au moment de la livraison est ce qui rend la
                      promesse crédible. Personne ne rembourse un rapport qu'il
                      n'a pas encore lu.
                    */}
                    {txId !== null && <Remboursement txId={txId} />}
                  </>
                ) : (
                  /*
                    L'attente dure. Le dire est le minimum : neuf minutes de
                    silence sur un écran qui vient de débiter quelqu'un, sans
                    rien qui bouge, se lisent comme une panne. Cette ligne
                    n'invente aucun délai — elle nomme ce qui se passe et
                    rappelle que fermer ne perd rien.
                  */
                  <AttenteRedaction etape={etape} courriel={courriel && email.trim() !== ""} />
                )}
              </>
            ) : manque ? (
              <>
                <h3>Rapport non délivré</h3>
                <p className="fine">
                  {message ||
                    "Votre paiement a bien été confirmé, mais la rédaction de votre rapport a échoué."}
                </p>
                {/*
                  L'écran s'arrêtait ici — un constat, et rien à faire. Le
                  message parlait d'une relance automatique qui n'existait nulle
                  part : `livrer()` n'est appelée que par le webhook, et un
                  webhook ne repasse pas. L'acheteur restait donc devant une
                  promesse que le code ne pouvait pas tenir, sans le moindre
                  bouton pour en sortir.
                */}
                {!relanceClose && (
                  <>
                    <p style={{ marginTop: "14px" }}>
                      <button
                        type="button"
                        className="btn btn-brass"
                        disabled={relanceEnCours}
                        onClick={relancer}
                      >
                        {relanceEnCours ? "Reprise en cours…" : "Relancer la rédaction"}
                      </button>
                    </p>
                    <p className="fine">
                      La reprise prend jusqu'à neuf minutes. Si elle n'aboutit pas non plus, le
                      remboursement vous est dû, sans justification à fournir.
                    </p>
                  </>
                )}
                {/*
                  La sortie est offerte **ici aussi**, et pas seulement à côté
                  d'un rapport livré. Un acheteur débité qui n'a rien reçu est
                  celui à qui le remboursement est le plus dû, et c'était le
                  seul écran d'où il ne pouvait pas le demander.
                */}
                {txId !== null && <Remboursement txId={txId} />}
              </>
            ) : perdu ? (
              <>
                <h3>Paiement non abouti</h3>
                <p className="fine">{message || "Aucun débit n'a eu lieu."}</p>
              </>
            ) : (
              <>
                <h3>En attente de votre confirmation</h3>
                <p className="fine">
                  Terminez le paiement sur la page qui vient de s'ouvrir, puis validez la demande sur
                  votre téléphone. Cette page se met à jour toute seule — nous n'affichons
                  « confirmé » que lorsque l'opérateur nous l'a dit.
                </p>
                {/*
                  Le lien de secours, toujours visible.

                  Il ne coûte rien à qui n'en a pas besoin, et il sauve tout le
                  monde à qui l'onglet a échappé — ce qui, dans un navigateur
                  intégré, arrive à beaucoup de gens. Sans lui, la seule issue
                  était de recommencer, en croyant avoir déjà payé.
                */}
                {urlPaiement !== null && (
                  <p className="fine">
                    <a href={urlPaiement} target="_blank" rel="noopener noreferrer">
                      La page de paiement ne s'est pas ouverte ? Rouvrez-la ici.
                    </a>
                  </p>
                )}
                {/*
                  L'ancien message promettait que « le rapport vous parviendra
                  quand même ». Tant que la livraison par messagerie n'est pas
                  branchée, il n'existe aucun canal par lequel il pourrait
                  parvenir : c'était un faux signal, au sens de la règle non
                  négociable n° 6. On dit maintenant où le retrouver — cette
                  page, qui se souvient de la transaction — et rien de plus.
                */}
                {expire && !simule && (
                  <>
                    <p className="fine">
                      Toujours rien de la part de l'opérateur. Vous pouvez fermer cette fenêtre sans
                      risque : le bouton <b>Récupérer mon rapport</b> vous ramènera ici, sur cette
                      même transaction, aussi longtemps qu'elle vaut.
                      {CONFIG.messagerieActive
                        ? " Le rapport partira aussi sur WhatsApp dès qu'il sera prêt."
                        : ""}
                    </p>
                    {/*
                      La référence, ici et pas seulement à la livraison.

                      C'est l'écran du motif d'échec le plus fréquent : sur
                      trente échecs relevés, dix sont des gens qui voulaient
                      payer et n'ont pas validé la demande sur leur téléphone.
                      Ceux-là valident souvent plus tard — après avoir fermé
                      l'onglet, parfois depuis un autre appareil. Le paiement
                      aboutit alors côté opérateur, le rapport se rédige, et
                      personne ne vient le chercher.

                      La phrase précédente disait « votre paiement est retenu
                      sur cet appareil », ce qui était exact et ce qui était le
                      problème : sur **cet** appareil, et nulle part ailleurs.
                      La référence lève cette limite au moment précis où elle
                      commence à peser.
                    */}
                    {txId !== null && <Reference txId={txId} attente />}
                  </>
                )}
                {simule && (
                  <div className="simpanel">
                    <p className="fine">
                      Choisissez l'issue à simuler. Le serveur émettra la notification signée
                      correspondante, exactement comme le ferait un opérateur.
                    </p>
                    {/*
                      Le site est public : sans ce code, quiconque le trouve
                      obtiendrait gratuitement le rapport vendu 3 000 F.
                    */}
                    <div className="field">
                      <label htmlFor="simcode">Code d'accès à la simulation</label>
                      <input
                        type="password"
                        id="simcode"
                        inputMode="numeric"
                        autoComplete="off"
                        value={codeSimule}
                        onChange={(e) => setCodeSimule(e.target.value)}
                        aria-invalid={erreurCode !== null}
                        aria-describedby={erreurCode ? "simcode-erreur" : undefined}
                      />
                      {erreurCode && (
                        <p id="simcode-erreur" className="fine" style={{ color: "var(--high)" }} role="alert">
                          {erreurCode}
                        </p>
                      )}
                    </div>
                    <div className="share-opts">
                      <button
                        type="button"
                        className="btn btn-brass"
                        disabled={simulationEnCours}
                        onClick={() => void declencher("reussi")}
                      >
                        Paiement réussi
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={simulationEnCours}
                        onClick={() => void declencher("echoue")}
                      >
                        Paiement échoué
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={simulationEnCours}
                        onClick={() => void declencher("annule")}
                      >
                        Annulé
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: "14px" }}
          onClick={fermer}
        >
          {step === 3 ? "Fermer" : "Annuler"}
        </button>
      </div>
    </div>
  );
}

/**
 * La référence de paiement, montrée et copiable.
 *
 * Volontairement sobre : ce n'est pas une récompense, c'est une clé. Le texte
 * dit ce qu'elle ouvre et depuis où, parce qu'une suite de caractères sans
 * explication ne se recopie pas — et c'est précisément quand on ne l'a pas
 * recopiée qu'on en a besoin.
 */
/**
 * La carte du temps récupéré, proposée à l'acheteur.
 *
 * Elle se compose sur l'appareil, comme la carte d'indice, et **rien ne part
 * sans un geste** : le partage ouvre WhatsApp avec un message pré-rempli que
 * la personne envoie, ou non. Sur un marché où la méfiance envers ce qui
 * « poste tout seul » est forte, c'est ce qui protège la marque autant que
 * l'utilisateur.
 *
 * Deux boutons plutôt qu'un : partager, et enregistrer. Le second existe pour
 * le statut WhatsApp, où l'on publie une image seule — c'est là que la carte
 * circule le plus, et c'est pour lui que le pied porte désormais une adresse
 * qu'on peut recopier.
 */
/**
 * Les trois étapes de la rédaction, en français.
 *
 * Les clés sont celles que `marquerEtape` écrit côté serveur. Une étape
 * inconnue n'affiche rien plutôt qu'un intitulé inventé : le jour où une
 * quatrième apparaît, l'écran se tait au lieu de mentir.
 */
const ETAPES: Record<string, string> = {
  redaction: "Vos dix actions sont en cours d'écriture.",
  pdf: "Votre document est en cours de mise en page.",
  rangement: "Votre document est prêt, nous le rangeons.",
};

function CarteProgression({ heures, jours, metier }: { heures: number; jours: number; metier: string }) {
  const [carte, setCarte] = useState<Blob | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void dessinerCarteProgression({
      heures,
      jours,
      metier,
      hote: window.location.host,
      chemin: cheminDeParrainage(),
    }).then((blob) => {
      if (vivant) setCarte(blob);
    });
    return () => {
      vivant = false;
    };
  }, [heures, jours, metier]);

  useEffect(() => {
    if (carte === null) return;
    const url = URL.createObjectURL(carte);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [carte]);

  if (carte === null) return null;
  const heuresFr = heures.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
  const texte = `${heuresFr} h rendues par semaine. ${phraseCarteProgression(jours)}`;

  return (
    <div className="carte-gain" style={{ marginTop: "18px" }}>
      <p className="mini">
        Ce que vous déclarez avoir repris, en une image. Elle porte la mention « heures déclarées » : c'est
        ce qui la rend honnête à montrer.
      </p>
      {apercu !== null && (
        <img
          src={apercu}
          alt={`Carte à partager : ${heuresFr} heures par semaine rendues en ${jours} jours, déclarées`}
          style={{ width: "100%", maxWidth: "320px", borderRadius: "10px", display: "block" }}
        />
      )}
      <p style={{ marginTop: "12px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            track("carte_gain_partage", { heures, progression: true });
            void partagerImage(carte, texte, nomCarteProgression(heures));
          }}
        >
          Partager
        </button>
      </p>
    </div>
  );
}

function CarteGain({ heures, metier }: { heures: number; metier: string }) {
  const [carte, setCarte] = useState<Blob | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void dessinerCarteGain({
      heures,
      metier,
      hote: window.location.host,
      chemin: cheminDeParrainage(),
    }).then((blob) => {
      if (vivant) setCarte(blob);
    });
    return () => {
      vivant = false;
    };
  }, [heures, metier]);

  /*
    L'aperçu est une URL d'objet : elle se révoque, sans quoi le blob reste en
    mémoire tant que l'onglet vit. Sur un téléphone modeste, à la fin d'un
    parcours d'achat, ce n'est pas une abstraction.
  */
  useEffect(() => {
    if (carte === null) return;
    const url = URL.createObjectURL(carte);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [carte]);

  if (carte === null) return null;

  const texte = `Mon rapport STRATERA m'annonce ${heures.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h par semaine récupérées, tâche par tâche.`;

  return (
    <div className="carte-gain" style={{ marginTop: "18px" }}>
      <p className="mini">
        Ce que votre rapport annonce vous rendre, en une image. À partager, ou pas.
      </p>
      {apercu !== null && (
        <img
          src={apercu}
          alt={`Carte à partager : ${heures} heures par semaine récupérées`}
          style={{ width: "100%", maxWidth: "320px", borderRadius: "10px", display: "block" }}
        />
      )}
      <p style={{ marginTop: "12px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            track("carte_gain_partage", { heures });
            void partagerImage(carte, texte, nomCarteGain(heures));
          }}
        >
          Partager
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            track("carte_gain_enregistree", { heures });
            const lien = document.createElement("a");
            lien.href = URL.createObjectURL(carte);
            lien.download = nomCarteGain(heures);
            lien.click();
            URL.revokeObjectURL(lien.href);
          }}
        >
          Enregistrer l'image
        </button>
      </p>
    </div>
  );
}

function Reference({ txId, attente = false }: { txId: string; attente?: boolean }) {
  const [copie, setCopie] = useState(false);

  /*
    Deux moments, deux phrases. À la livraison, la référence rouvre un rapport
    qui existe. Sur l'écran d'attente, il n'existe pas encore — dire « elle
    rouvre votre rapport » y serait faux, et faux exactement pour la personne
    qui n'est pas sûre d'avoir payé.
  */
  const explication = attente
    ? "Notez-la avant de fermer. Si vous validez la demande plus tard, ou depuis un autre téléphone, c'est elle qui vous ramènera à ce paiement — nous ne demandons ni compte ni adresse."
    : "Notez-la quelque part. Nous ne demandons ni compte ni adresse, donc c'est la seule chose qui rouvre votre rapport depuis un autre appareil.";

  return (
    <div className="reference-achat">
      <div className="k">Votre référence</div>
      <code>{txId}</code>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => {
          void navigator.clipboard
            .writeText(txId)
            .then(() => setCopie(true))
            .catch(() => setCopie(false));
        }}
      >
        {copie ? "Référence copiée" : "Copier la référence"}
      </button>
      <p className="fine" aria-live="polite">
        {copie ? "Gardez-la : elle vaut compte et mot de passe, sur n'importe quel appareil." : explication}
      </p>
    </div>
  );
}
